import React, { useState, useEffect } from 'react';
import { 
  Search, Edit, Trash2, UserPlus, Users, ChevronLeft, ChevronRight,
  UserCheck, Shield, Building, Truck, AlertCircle, X, Filter
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

export default function UserList() {
  // Estados principales
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Estados de filtros
  const [filters, setFilters] = useState({
    searchTerm: '',
    userType: '',
    clienteProveedor: ''
  });
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Estados de UI
  const [message, setMessage] = useState({ text: '', type: '' });
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, user: null });

  // Cargar usuarios
  const loadUsers = async (page = 1, currentFilters = filters) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const queryParams = new URLSearchParams({
        page: page,
        limit: itemsPerPage,
        search: currentFilters.searchTerm || '',
        userType: currentFilters.userType || '',
        clienteProveedor: currentFilters.clienteProveedor || ''
      });

      const response = await fetch(`${API_URL}/api/usuarios?${queryParams}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Error al cargar usuarios');
      }
      
      const data = await response.json();
      setUsers(data.usuarios || []);
      setTotalUsers(data.total || 0);
      
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      showMessage('Error al cargar los usuarios', 'danger');
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos iniciales
  useEffect(() => {
    loadUsers(currentPage, filters);
  }, [currentPage]);

  // Manejar cambios en filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    setCurrentPage(1);
    loadUsers(1, filters);
  };

  // Limpiar filtros
  const handleClearFilters = () => {
    const clearedFilters = {
      searchTerm: '',
      userType: '',
      clienteProveedor: ''
    };
    setFilters(clearedFilters);
    setCurrentPage(1);
    loadUsers(1, clearedFilters);
  };

  // Eliminar usuario
  const handleDeleteUser = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      
      const response = await fetch(`${API_URL}/api/usuarios/${userId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Error al eliminar usuario');
      }
      
      showMessage('Usuario eliminado correctamente', 'success');
      setDeleteConfirm({ show: false, user: null });
      loadUsers(currentPage, filters);
      
    } catch (error) {
      console.error('Error al eliminar usuario:', error);
      showMessage('Error al eliminar el usuario', 'danger');
    }
  };

  // Utilidades
  const showMessage = (text, type) => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 8080);
  };

  const getUserTypeLabel = (permisos) => {
    switch (permisos) {
      case 1: return { label: 'Super Administrador', icon: Shield, color: '#dc2626' };
      case 2: return { label: 'Administrador', icon: UserCheck, color: '#ea580c' };
      case 4: return { label: 'Proveedor', icon: Truck, color: '#7c3aed' };
      case 5: return { label: 'Cliente', icon: Building, color: '#4DD5AF' };
      default: return { label: 'Usuario', icon: Users, color: '#6b7280' };
    }
  };

  const getUserTypeOptions = () => [
    { value: '', label: 'Todos los tipos' },
    { value: '1', label: 'Super Administrador' },
    { value: '2', label: 'Administrador' },
    { value: '4', label: 'Proveedor' },
    { value: '5', label: 'Cliente' }
  ];

  const totalPages = Math.ceil(totalUsers / itemsPerPage);

  // Componente de confirmación de eliminación
  const DeleteConfirmModal = () => {
    if (!deleteConfirm.show) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 transform transition-all">
          <div className="p-6">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="ml-4 text-xl font-bold text-gray-900">Confirmar Eliminación</h3>
            </div>
            
            <p className="text-gray-600 mb-6">
              ¿Estás seguro de eliminar al usuario <strong>"{deleteConfirm.user?.nombrec}"</strong>?
              <span className="block text-sm text-red-600 mt-2 font-semibold">Esta acción no se puede deshacer.</span>
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm({ show: false, user: null })}
                className="flex-1 px-4 py-2 border-2 rounded-lg font-medium transition-colors"
                style={{borderColor: '#4DD5AF', backgroundColor: '#4DD5AF20', color: '#4DD5AF'}}
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm.user.id)}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium flex items-center justify-center"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && users.length === 0) {
    return (
      <div className="min-h-screen bg-white flex justify-center items-center">
        <div className="text-center">
          <div className="relative mb-8">
            <div className="w-20 h-20 border-4 rounded-full animate-spin border-t-teal-600 mx-auto" style={{borderColor: '#4DD5AF40', borderTopColor: '#4DD5AF'}}></div>
            <Users className="w-8 h-8 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" style={{color: '#4DD5AF'}} />
          </div>
          <h3 className="text-xl font-bold mb-2" style={{color: '#4DD5AF'}}>Cargando usuarios</h3>
          <p className="text-gray-600">Obteniendo lista de usuarios...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="shadow-lg border-b-2 bg-white" style={{borderBottomColor: '#4DD5AF'}}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-lg" style={{backgroundColor: '#4DD5AF'}}>
                <Users className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-800">Gestión de Usuarios</h1>
                <p className="text-sm font-medium" style={{color: '#4DD5AF'}}>Administración de accesos del sistema</p>
              </div>
            </div>
            
            <button 
              onClick={() => window.location.href = 'usuarios/nuevo'}
              className="px-4 py-2 rounded-lg font-medium transition-all duration-300 flex items-center text-sm text-white shadow-lg transform hover:scale-105 hover:opacity-90"
              style={{backgroundColor: '#4DD5AF'}}
            >
              <UserPlus className="w-4 h-4 mr-2" />
              Nuevo Usuario
            </button>
          </div>
        </div>
      </div>

      {/* Info Bar */}
      <div className="text-white" style={{background: `linear-gradient(to right, #4DD5AF, #34d399, #10b981)`}}>
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Users className="w-4 h-4" />
              <span className="font-bold">Lista de Usuarios</span>
            </div>
          </div>
        </div>
      </div>

      {/* Mensaje de estado */}
      {message.text && (
        <div className="max-w-7xl mx-auto px-6 pt-4">
          <div className={`p-3 rounded-lg flex items-center shadow-lg border text-sm ${
            message.type === 'success' ? 'border-emerald-200' : 'border-red-200'
          }`}
          style={{
            background: message.type === 'success' ? 'linear-gradient(135deg, #ecfdf5, #d1fae5)' : 'linear-gradient(135deg, #fef2f2, #fecaca)',
            color: message.type === 'success' ? '#065f46' : '#991b1b'
          }}>
            <AlertCircle className="w-4 h-4 mr-2 flex-shrink-0" />
            <span className="font-medium flex-1">{message.text}</span>
            <button 
              onClick={() => setMessage({ text: '', type: '' })}
              className="ml-auto p-1 hover:bg-white hover:bg-opacity-30 rounded"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}

      {/* Contenido Principal */}
      <div className="max-w-7xl mx-auto px-6 py-6">
        
        {/* Formulario de Filtros */}
        <div className="bg-white rounded-xl shadow-lg border-2 mb-6" style={{borderColor: '#4DD5AF'}}>
          <div className="p-3" style={{backgroundColor: '#4DD5AF'}}>
            <h3 className="text-lg font-bold text-white flex items-center">
              <Filter className="w-5 h-5 mr-2" />
              Filtros de Búsqueda
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              {/* Búsqueda por nombre/usuario */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Buscar por nombre o usuario:
                </label>
                <input
                  type="text"
                  value={filters.searchTerm}
                  onChange={(e) => handleFilterChange('searchTerm', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyFilters();
                    }
                  }}
                  placeholder="Nombre completo o usuario..."
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>

              {/* Filtro por tipo de usuario */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Tipo de usuario:
                </label>
                <select
                  value={filters.userType}
                  onChange={(e) => handleFilterChange('userType', e.target.value)}
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                >
                  {getUserTypeOptions().map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Filtro por cliente/proveedor */}
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Cliente/Proveedor:
                </label>
                <input
                  type="text"
                  value={filters.clienteProveedor}
                  onChange={(e) => handleFilterChange('clienteProveedor', e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleApplyFilters();
                    }
                  }}
                  placeholder="Nombre del cliente o proveedor..."
                  className="w-full px-3 py-2 border-2 rounded-lg focus:outline-none transition-all bg-white"
                  style={{borderColor: '#e5e7eb'}}
                  onFocus={(e) => e.target.style.borderColor = '#4DD5AF'}
                  onBlur={(e) => e.target.style.borderColor = '#e5e7eb'}
                />
              </div>
            </div>

            {/* Botones de acción */}
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-6 py-2 border-2 rounded-lg font-medium transition-all flex items-center"
                style={{borderColor: '#6b7280', backgroundColor: '#f9fafb', color: '#6b7280'}}
              >
                <X className="w-4 h-4 mr-2" />
                Limpiar
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                className="px-6 py-2 rounded-lg font-medium transition-all flex items-center text-white"
                style={{backgroundColor: '#4DD5AF'}}
              >
                <Search className="w-4 h-4 mr-2" />
                Aplicar Filtros
              </button>
            </div>
          </div>
        </div>

        {/* Tabla de Usuarios */}
        <div className="bg-white rounded-xl shadow-lg border-2 overflow-hidden" style={{borderColor: '#4DD5AF'}}>
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead 
                className="text-white"
                style={{
                  backgroundColor: '#4DD5AF',
                  backgroundImage: 'linear-gradient(to right, #4DD5AF, #34d399)',
                }}
              >
                <tr style={{ pointerEvents: 'none' }}>
                  <th className="px-4 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">ID</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Usuario</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Nombre Completo</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Tipo de Usuario</th>
                  <th className="px-4 py-4 text-left text-sm font-bold text-white uppercase tracking-wider">Cliente/Proveedor</th>
                  <th className="px-4 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center">
                        <Users className="w-16 h-16 text-gray-300 mb-4" />
                        <h3 className="text-xl font-bold text-gray-500 mb-2">No hay usuarios</h3>
                        <p className="text-gray-400">No se encontraron usuarios con los criterios de búsqueda</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  users.map((user, index) => {
                    const userType = getUserTypeLabel(user.permisos);
                    const IconComponent = userType.icon;
                    
                    return (
                      <tr 
                        key={user.id}
                        className={`hover:bg-gray-50 transition-colors ${
                          index % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                        }`}
                      >
                        <td className="px-4 py-4 text-sm font-bold text-gray-900">{user.id}</td>
                        <td className="px-4 py-4 text-sm font-medium text-gray-900">{user.username}</td>
                        <td className="px-4 py-4 text-sm text-gray-900">{user.nombrec}</td>
                        <td className="px-4 py-4 text-sm">
                          <div className="flex items-center">
                            <IconComponent 
                              className="w-4 h-4 mr-2" 
                              style={{color: userType.color}} 
                            />
                            <span className="font-medium" style={{color: userType.color}}>
                              {userType.label}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-gray-600">
                          {user.clinica_matriz_nombre || user.proveedor_nombre || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <button
                              onClick={() => window.location.href = `?sec=usuarios/actualizar&uid=${user.id}`}
                              className="p-2 rounded-lg transition-colors border"
                              style={{backgroundColor: '#4DD5AF20', borderColor: '#4DD5AF40', color: '#4DD5AF'}}
                              title="Editar usuario"
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ show: true, user: user })}
                              className="p-2 rounded-lg transition-colors border border-red-200 text-red-600 hover:bg-red-50"
                              title="Eliminar usuario"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Paginación */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-600">
              Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalUsers)} de {totalUsers} usuarios
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderColor: currentPage === 1 ? '#e5e7eb' : '#4DD5AF',
                  backgroundColor: currentPage === 1 ? '#f9fafb' : '#4DD5AF20',
                  color: currentPage === 1 ? '#6b7280' : '#4DD5AF'
                }}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              {[...Array(totalPages)].map((_, i) => (
                <button
                  key={i + 1}
                  onClick={() => setCurrentPage(i + 1)}
                  className={`px-3 py-2 rounded-lg border transition-colors ${
                    currentPage === i + 1 
                      ? 'text-white font-bold' 
                      : 'font-medium'
                  }`}
                  style={{
                    borderColor: '#4DD5AF',
                    backgroundColor: currentPage === i + 1 ? '#4DD5AF' : '#4DD5AF20',
                    color: currentPage === i + 1 ? '#ffffff' : '#4DD5AF'
                  }}
                >
                  {i + 1}
                </button>
              ))}
              
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  borderColor: currentPage === totalPages ? '#e5e7eb' : '#4DD5AF',
                  backgroundColor: currentPage === totalPages ? '#f9fafb' : '#4DD5AF20',
                  color: currentPage === totalPages ? '#6b7280' : '#4DD5AF'
                }}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de confirmación de eliminación */}
      <DeleteConfirmModal />
    </div>
  );
}